import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "react-query";
import axios from "axios";
import { useToast } from "../useToast";
import { useSubmitLineup } from "./useSubmitLineup";
import { useFetchUserEventLineup } from "./useFetchUserEventLineup";
import { calculateScoreForSlot } from "../../utils/events/FighterScores";
import {
  setLineup as setReduxLineup,
  setLotn as setReduxLotn,
  setAlternateSelections as setReduxAlternateSelections,
  setAlternateLotn as setReduxAlternateLotn,
  setProjectedTotal as setReduxProjectedTotal,
  saveDraft,
  loadDraft,
  clearDraft,
} from "../../features/lineup/lineupReducer";

/**
 * Custom hook that encapsulates all lineup submission logic
 * @param {Object} options - Configuration options
 * @param {string} options.eventId - ID of the event
 * @param {string} [options.leagueId] - Optional league ID override
 * @param {string} [options.userId] - Optional user ID override
 * @param {Function} [options.onSubmissionSuccess] - Callback fired after successful submission
 * @returns {Object} State and methods for lineup management
 */
const useLineupSubmission = ({
  eventId,
  leagueId: leagueIdProp,
  userId: userIdProp,
  onSubmissionSuccess,
}) => {
  // Redux integration
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const userIdFromRedux = useSelector((state) => state.user.userId);
  const leagueIdFromRedux = useSelector((state) => state.league.leagueId);
  const eventName = useSelector((state) => state.event.eventName);

  // Use prop values if provided, otherwise fall back to Redux
  const userId = userIdProp || userIdFromRedux;
  const leagueId = leagueIdProp || leagueIdFromRedux;

  // Global league ID from env
  const globalLeagueId = process.env.REACT_APP_GLOBAL_LEAGUE_ID;

  // Get lineup data from Redux
  const lineup = useSelector((state) => state.lineup.lineup);
  const lotn = useSelector((state) => state.lineup.lotn);
  const alternates = useSelector((state) => state.lineup.alternateSelections);
  const alternateLotn = useSelector((state) => state.lineup.alternateLOTN);
  const projectedTotal = useSelector((state) => state.lineup.projectedTotal);
  const drafts = useSelector((state) => state.lineup.drafts);
  const currentDraftKey = useSelector((state) => state.lineup.currentDraftKey);

  // Local state for UI
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showGlobalSyncModal, setShowGlobalSyncModal] = useState(false);
  const [savedSubmissionData, setSavedSubmissionData] = useState(null);
  // Tracks whether the global sync modal is for updating an existing global lineup vs creating a new one
  const [isGlobalSyncUpdate, setIsGlobalSyncUpdate] = useState(false);

  // Generate draft key for current league-event combination
  const draftKey = `${leagueId}-${eventId}`;

  // API integration
  const { mutate: submitLineup, isLoading: isSubmitLoading } =
    useSubmitLineup();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // Fetch existing submission
  const {
    data: existingSubmission,
    isLoading: isLoadingSubmission,
    error: submissionError,
  } = useFetchUserEventLineup(
    leagueId,
    eventId,
    userId,
    Boolean(leagueId && eventId && userId),
  );

  // Initialize from existing submission OR load draft when switching leagues/events
  useEffect(() => {
    // Don't do anything while loading
    if (isLoadingSubmission) {
      return;
    }

    // Check if we're switching to a different league-event combination
    const isDifferentContext = currentDraftKey !== draftKey;

    // Priority 1: If we have an API submission, load it
    if (
      existingSubmission &&
      existingSubmission.selections &&
      existingSubmission.selections.length > 0
    ) {
      const {
        selections,
        alternates: altSelections,
        projectedTotal: total,
      } = existingSubmission;

      // Populate lineup
      const newLineup = Array(5).fill(null);
      selections.forEach((selection, index) => {
        if (index < 5) {
          newLineup[index] = {
            FighterId: Number(selection.fighterId),
            FirstName: selection.fighter.split(" ")[0],
            LastName:
              selection.fighter.split(" ").slice(1).join(" ") ||
              selection.fighter.split(" ")[1],
            Moneyline: selection.moneyline,
          };
        }
      });
      dispatch(setReduxLineup(newLineup));

      // Set projected total
      if (total) {
        dispatch(setReduxProjectedTotal(total));
      }

      // Set LOTN
      const lotnFighter = selections.find((selection) => selection.lotn);
      if (lotnFighter) {
        dispatch(setReduxLotn(Number(lotnFighter.fighterId)));
      } else {
        dispatch(setReduxLotn(null));
      }

      // Set alternate LOTN - check selections first, then fall back to alternates
      // Set alternate LOTN - check top-level field first, then selections, then alternates
      const topLevelAltLotn = existingSubmission.alternateLotn;
      const altLotnFromSelections = selections.find(
        (selection) => selection.alternateLotn,
      );
      const altLotnFromAlternates =
        altSelections &&
        altSelections.find((alt) => alt.alternateLotn || alt.lotn);
      if (topLevelAltLotn) {
        dispatch(setReduxAlternateLotn(Number(topLevelAltLotn)));
      } else if (altLotnFromSelections) {
        dispatch(
          setReduxAlternateLotn(Number(altLotnFromSelections.fighterId)),
        );
      } else if (altLotnFromAlternates) {
        dispatch(
          setReduxAlternateLotn(Number(altLotnFromAlternates.fighterId)),
        );
      } else {
        dispatch(setReduxAlternateLotn(null));
      }

      // Set alternates
      if (altSelections && altSelections.length) {
        const formattedAlternates = altSelections.map((alternate) => ({
          FighterId: Number(alternate.fighterId),
          FirstName: alternate.fighter.split(" ")[0],
          LastName:
            alternate.fighter.split(" ").slice(1).join(" ") ||
            alternate.fighter.split(" ")[1],
          Moneyline: alternate.moneyline,
        }));
        dispatch(setReduxAlternateSelections(formattedAlternates));
      } else {
        dispatch(setReduxAlternateSelections([]));
      }

      setIsInitialized(true);
    }
    // Priority 2: If no submission but we have a draft, load it
    else if (drafts[draftKey] && (!isInitialized || isDifferentContext)) {
      dispatch(loadDraft(leagueId, eventId));
      setIsInitialized(true);
    }
    // Priority 3: No submission and no draft - clear to empty state
    else if (isDifferentContext || !isInitialized) {
      dispatch(setReduxLineup(Array(5).fill(null)));
      dispatch(setReduxLotn(null));
      dispatch(setReduxAlternateSelections([]));
      dispatch(setReduxAlternateLotn(null));
      dispatch(setReduxProjectedTotal(0));
      setIsInitialized(true);
    }
  }, [
    existingSubmission,
    isLoadingSubmission,
    dispatch,
    leagueId,
    eventId,
    draftKey,
    currentDraftKey,
    drafts,
    isInitialized,
  ]);

  // Calculate projected total function
  const calculateProjectedTotal = useCallback(() => {
    const total = lineup.reduce((acc, fighter, index) => {
      if (!fighter) return acc;

      let score = calculateScoreForSlot(fighter.Moneyline, index);
      if (fighter.FighterId === lotn) {
        score *= 2; // Double for LOTN
      }

      return acc + score;
    }, 0);

    dispatch(setReduxProjectedTotal(total));
  }, [lineup, lotn, dispatch]);

  // Calculate projected total when lineup or LOTN changes
  useEffect(() => {
    calculateProjectedTotal();
  }, [calculateProjectedTotal]);

  // Auto-save draft whenever lineup changes (but only if no submission exists)
  useEffect(() => {
    // Don't save draft if:
    // - Still loading
    // - Not initialized yet
    // - A submission already exists (we're editing a submitted lineup)
    if (isLoadingSubmission || !isInitialized) {
      return;
    }

    // If there's an existing submission with selections, don't auto-save drafts
    const hasExistingSubmission =
      existingSubmission &&
      existingSubmission.selections &&
      existingSubmission.selections.length > 0;

    if (hasExistingSubmission) {
      return;
    }

    // Check if there's any data to save (not all empty)
    const hasData =
      lineup.some((f) => f !== null) ||
      alternates.length > 0 ||
      lotn !== null ||
      alternateLotn !== null;

    if (hasData) {
      // Save current state as draft
      const draftData = {
        lineup,
        alternateSelections: alternates,
        lotn,
        alternateLOTN: alternateLotn,
        projectedTotal,
      };
      dispatch(saveDraft(leagueId, eventId, draftData));
    }
  }, [
    lineup,
    alternates,
    lotn,
    alternateLotn,
    projectedTotal,
    leagueId,
    eventId,
    draftKey,
    dispatch,
    isLoadingSubmission,
    isInitialized,
    existingSubmission,
  ]);

  // Actions

  /**
   * Swap a fighter from one position to another.
   * Scores recalculate automatically since they're index-based.
   */
  const moveFighter = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= lineup.length) return;
    const newLineup = [...lineup];
    [newLineup[fromIndex], newLineup[toIndex]] = [newLineup[toIndex], newLineup[fromIndex]];
    dispatch(setReduxLineup(newLineup));
  };

  const addFighter = (position, fighter) => {
    const newLineup = [...lineup];
    newLineup[position] = fighter;
    dispatch(setReduxLineup(newLineup));
  };

  const removeFighter = (position) => {
    const newLineup = [...lineup];
    const fighter = newLineup[position];

    if (fighter) {
      // Clear LOTN if this fighter was the LOTN
      if (fighter.FighterId === lotn) {
        dispatch(setReduxLotn(null));
      }

      newLineup[position] = null;
      dispatch(setReduxLineup(newLineup));
    }
  };

  const toggleLotn = (fighterId) => {
    dispatch(setReduxLotn(lotn === fighterId ? null : fighterId));
  };

  const addAlternate = (fighter) => {
    if (
      alternates.length < 2 &&
      !alternates.some((alt) => alt?.FighterId === fighter.FighterId)
    ) {
      dispatch(setReduxAlternateSelections([...alternates, fighter]));
    } else {
      toast.error("You can only select up to two alternates");
    }
  };

  const removeAlternate = (fighterId) => {
    const newAlternates = alternates.filter(
      (alt) => alt.FighterId !== fighterId,
    );
    dispatch(setReduxAlternateSelections(newAlternates));

    // Clear alternate LOTN if needed
    if (alternateLotn === fighterId) {
      dispatch(setReduxAlternateLotn(null));
    }
  };

  const toggleAlternateLotn = (fighter) => {
    const fighterId = fighter.FighterId;
    const isSelected = alternateLotn === fighterId;
    dispatch(setReduxAlternateLotn(isSelected ? null : fighterId));
  };

  const removeAll = () => {
    // Clear all lineup state
    dispatch(setReduxLineup(Array(5).fill(null)));
    dispatch(setReduxLotn(null));
    dispatch(setReduxAlternateSelections([]));
    dispatch(setReduxAlternateLotn(null));
    dispatch(setReduxProjectedTotal(0));

    // Clear the draft for this league-event combination
    dispatch(clearDraft(leagueId, eventId));
  };

  const validateLineup = () => {
    // Check all positions filled
    if (!lineup.every((fighter) => fighter)) {
      toast.warning("Please select fighters for all positions");
      return false;
    }

    // Check LOTN selected
    if (!lotn) {
      toast.warning("Please select a Lock of the Night");
      return false;
    }

    // Check alternates selected
    if (alternates.length < 2) {
      toast.warning("Please select two alternate fighters");
      return false;
    }

    // Check alternate LOTN selected
    if (alternateLotn === null) {
      toast.warning("Please select an Alternate Lock of the Night");
      return false;
    }

    return true;
  };

  const handleSubmitButtonClick = () => {
    if (validateLineup()) {
      setShowSubmissionModal(true);
    }
  };

  const handleConfirmSubmit = () => {
    setShowSubmissionModal(false);
    submitLineupToApi();
  };

  /**
   * Compare the current league submission selections with the global submission.
   * Returns true if the lineups are identical (same fighters, positions, LOTN, alternates).
   */
  const areLineupsEqual = (localSelections, localAlternates, localAlternateLotn, globalSubmission) => {
    const globalSelections = globalSubmission.selections || [];
    const globalAlternates = globalSubmission.alternates || [];

    if (localSelections.length !== globalSelections.length) return false;
    if (localAlternates.length !== globalAlternates.length) return false;

    // Compare primary selections by position
    for (const local of localSelections) {
      const match = globalSelections.find(
        (g) =>
          Number(g.fighterId) === Number(local.fighterId) &&
          g.position === local.position &&
          g.lotn === local.lotn,
      );
      if (!match) return false;
    }

    // Compare alternates (order-independent)
    const localAltIds = localAlternates.map((a) => Number(a.fighterId)).sort();
    const globalAltIds = globalAlternates.map((a) => Number(a.fighterId)).sort();
    if (localAltIds.join(",") !== globalAltIds.join(",")) return false;

    // Compare alternate LOTN
    const globalAltLotn = globalSubmission.alternateLotn;
    if (Number(localAlternateLotn || 0) !== Number(globalAltLotn || 0)) return false;

    return true;
  };

  const submitLineupToApi = async () => {
    // Prepare submission data
    const selections = lineup.map((fighter, index) => ({
      fighter: `${fighter.FirstName} ${fighter.LastName}`,
      fighterId: fighter.FighterId,
      moneyline: fighter.Moneyline,
      score: calculateScoreForSlot(fighter.Moneyline, index),
      lotn: fighter.FighterId === lotn,
      alternateLotn: fighter.FighterId === alternateLotn,
      position: index + 1,
    }));

    const formattedAlternates = alternates.map((fighter) => ({
      fighter: `${fighter.FirstName} ${fighter.LastName}`,
      fighterId: fighter.FighterId,
      moneyline: fighter.Moneyline,
      alternateLotn: fighter.FighterId === alternateLotn,
    }));

    // Submit to API
    submitLineup(
      {
        userId,
        eventId,
        eventName,
        selections,
        leagueId,
        alternates: formattedAlternates,
        projectedTotal,
        alternateLotn,
      },
      {
        onSuccess: async () => {
          toast.success("Lineup submitted successfully!");

          // Clear the draft since we now have a submission
          dispatch(clearDraft(leagueId, eventId));

          // Check if this is NOT a global league submission
          if (leagueId !== globalLeagueId) {
            try {
              // Check if user has a global submission using the same endpoint as useFetchUserEventLineup
              const apiUrl = process.env.REACT_APP_API_URL;
              const token = await getAccessTokenSilently();
              const globalSubmission = await queryClient.fetchQuery(
                ["userLineup", globalLeagueId, eventId, userId],
                async () => {
                  const { data } = await axios.get(
                    `${apiUrl}/api/lineup/${globalLeagueId}/${eventId}/${userId}`,
                    { headers: { Authorization: `Bearer ${token}` } },
                  );
                  return data;
                },
              );

              const hasGlobalSubmission =
                globalSubmission &&
                globalSubmission.selections &&
                globalSubmission.selections.length > 0;

              if (!hasGlobalSubmission) {
                // No global submission — ask if they want to create one
                setSavedSubmissionData({
                  selections,
                  alternates: formattedAlternates,
                  projectedTotal,
                  alternateLotn,
                });
                setIsGlobalSyncUpdate(false);
                setShowGlobalSyncModal(true);
              } else if (
                !areLineupsEqual(selections, formattedAlternates, alternateLotn, globalSubmission)
              ) {
                // Global submission exists but differs from the edited lineup — ask to sync
                setSavedSubmissionData({
                  selections,
                  alternates: formattedAlternates,
                  projectedTotal,
                  alternateLotn,
                });
                setIsGlobalSyncUpdate(true);
                setShowGlobalSyncModal(true);
              } else {
                // Global submission exists and is identical — no prompt needed
                if (onSubmissionSuccess) onSubmissionSuccess();
                navigate("/events");
              }
            } catch (error) {
              // A 404 means no global submission exists - show the modal
              if (error?.response?.status === 404) {
                setSavedSubmissionData({
                  selections,
                  alternates: formattedAlternates,
                  projectedTotal,
                  alternateLotn,
                });
                setIsGlobalSyncUpdate(false);
                setShowGlobalSyncModal(true);
              } else {
                console.error("Error checking global submission:", error);
                if (onSubmissionSuccess) onSubmissionSuccess();
                navigate("/events");
              }
            }
          } else {
            // If this was a global submission, just navigate to events
            if (onSubmissionSuccess) onSubmissionSuccess();
            navigate("/events");
          }
        },
        onError: (error) => {
          toast.error(`Error submitting lineup: ${error.message}`);
        },
      },
    );
  };

  const handleGlobalSyncConfirm = () => {
    setShowGlobalSyncModal(false);

    // Use saved submission data (lineup may have been cleared after initial submit)
    if (!savedSubmissionData) {
      toast.error("Submission data not available. Please try again.");
      navigate("/events");
      return;
    }

    // Submit to global league using saved data
    submitLineup(
      {
        userId,
        eventId,
        eventName,
        selections: savedSubmissionData.selections,
        leagueId: globalLeagueId,
        alternates: savedSubmissionData.alternates,
        projectedTotal: savedSubmissionData.projectedTotal,
        alternateLotn: savedSubmissionData.alternateLotn,
      },
      {
        onSuccess: () => {
          toast.success(
            isGlobalSyncUpdate
              ? "Global Competition lineup updated!"
              : "Lineup submitted to Global Competition!",
          );
          setSavedSubmissionData(null);
          setIsGlobalSyncUpdate(false);
          if (onSubmissionSuccess) onSubmissionSuccess();
          navigate("/events");
        },
        onError: (error) => {
          toast.error(`Error submitting to global: ${error.message}`);
          setSavedSubmissionData(null);
          setIsGlobalSyncUpdate(false);
          if (onSubmissionSuccess) onSubmissionSuccess();
          navigate("/events");
        },
      },
    );
  };

  const handleGlobalSyncDecline = () => {
    setShowGlobalSyncModal(false);
    setSavedSubmissionData(null);
    setIsGlobalSyncUpdate(false);
    if (onSubmissionSuccess) onSubmissionSuccess();
    navigate("/events");
  };

  // Helper function to check if fighter or opponent is in lineup
  const isFighterOrOpponentInLineup = (fighterIndex, allFighters) => {
    if (
      !allFighters ||
      fighterIndex < 0 ||
      fighterIndex >= allFighters.length
    ) {
      return false;
    }

    const fighter = allFighters[fighterIndex];

    // Find opponent index (assumes fighters are paired sequentially in the array)
    const opponentIndex =
      fighterIndex % 2 === 0 ? fighterIndex + 1 : fighterIndex - 1;

    // Check if either fighter or opponent is in lineup
    return lineup.some(
      (selectedFighter) =>
        selectedFighter &&
        (selectedFighter.FighterId === fighter.FighterId ||
          (opponentIndex >= 0 &&
            opponentIndex < allFighters.length &&
            selectedFighter.FighterId ===
              allFighters[opponentIndex].FighterId)),
    );
  };

  return {
    // State
    lineup,
    lotn,
    alternates,
    alternateLotn,
    projectedTotal,
    showSubmissionModal,
    showGlobalSyncModal,
    isGlobalSyncUpdate,
    isLoading: isLoadingSubmission || isSubmitLoading,
    error: submissionError,

    // Computed properties
    isComplete:
      lineup.every((fighter) => fighter) &&
      lotn !== null &&
      alternates.length === 2 &&
      alternateLotn !== null,
    hasSubmission: Boolean(existingSubmission && existingSubmission.selections),

    // Actions
    addFighter,
    moveFighter,
    removeFighter,
    toggleLotn,
    addAlternate,
    removeAlternate,
    toggleAlternateLotn,
    removeAll,
    handleSubmitButtonClick,
    handleConfirmSubmit,
    setShowSubmissionModal,
    isFighterOrOpponentInLineup,
    handleGlobalSyncConfirm,
    handleGlobalSyncDecline,
  };
};

export default useLineupSubmission;
